import { find } from 'lodash';
import { getIntentCategories, getDefaultGetStartedApps } from './helpers';

const getSectionsForCategory = (key, sections, tools) => {
  sections = sections.filter(({ categoryKey = '' }) => categoryKey === key);
  sections = sections.map(s => {
    return {
      content: s,
      tools: getTools(s.sectionKey, tools)
    };
  });
  return sections;
};

const getTools = (key, tools) =>
  tools.filter(tool => tool.categoryKey === key || tool.sectionKey === key);

const isNotRecommendedStaffPicked = ({
  recommendedApps,
  staffPickAppKeys,
  intentRowKeys
}) =>
  recommendedApps.length === 0 &&
  staffPickAppKeys.length === 0 &&
  intentRowKeys.length === 0;

const isNotRecommendedStaffPickedCategory = (
  categoryKey,
  { recommendedCategories, staffPickCategory, intentRowCategory }
) =>
  !recommendedCategories.includes(categoryKey) &&
  !staffPickCategory.includes(categoryKey) &&
  !intentRowCategory.includes(categoryKey);

// if the category of either staff pick, intent row or recommendedApp is not in categoryKey,
// then the app should return the first 2 apps of the category.
// this way there is not need to travel to the rest of the logic
const shouldReturnFirst2Apps = (
  categoryKey,
  toolsForCategory,
  {
    recommendedApps,
    staffPickAppKeys,
    intentRowKeys,
    recommendedCategories,
    staffPickCategory,
    intentRowCategory
  },
  numRecommendations
) =>
  toolsForCategory.length <= numRecommendations ||
  isNotRecommendedStaffPicked({
    recommendedApps,
    staffPickAppKeys,
    intentRowKeys
  }) ||
  isNotRecommendedStaffPickedCategory(categoryKey, {
    recommendedCategories,
    staffPickCategory,
    intentRowCategory
  });

const getFeaturedTools = (
  categoryKey,
  toolsForCategory,
  recommendations,
  {
    staffPickCategory,
    staffPickAppKeys,
    isLoggedIn,
    intentRowKeys,
    intentRowCategory,
    isStaffPickEligible,
    isNewLayoutEligible
  }
) => {
  const { recommendedCategories, recommendedApps } = recommendations;

  const numRecommendations = isNewLayoutEligible ? 6 : 2;

  const appsAndCategoriesObject = {
    recommendedApps,
    staffPickAppKeys,
    intentRowKeys,
    recommendedCategories,
    staffPickCategory,
    intentRowCategory
  };

  if (
    shouldReturnFirst2Apps(
      categoryKey,
      toolsForCategory,
      appsAndCategoriesObject,
      numRecommendations
    )
  ) {
    return toolsForCategory.slice(0, numRecommendations);
  }

  /*
    If an app within a pillar category is already in the `Recommended For You`
    section, choose the other apps to feature in that pillar - e.q. if `PayPal
    Checkout` is in `Recommended For You` section, `Accept Payments` pillar should
    feature the 2nd and 3rd ranking apps, such as `Invoicing` and `Subscriptions`.

    If there are no other apps in that pillar to feature, use the recommended app in the pillar
    anyway - e.q. if `Fraud Protection` is in `Recommended For You`
    section, it should still feature it in the `Risk` pillar as it is the only app).
  */
  const featuredTools = [];

  for (let i = 0; i < toolsForCategory.length; i++) {
    if (featuredTools.length === numRecommendations) {
      break;
    }

    const isRecommended = recommendedApps.includes(toolsForCategory[i].appKey);
    const isInStaffPick =
      isStaffPickEligible &&
      staffPickAppKeys.includes(toolsForCategory[i].appKey);
    const isIntent = intentRowKeys.includes(toolsForCategory[i].appKey);
    //The app should not be in both staff pick and recommended row in order to display In the category section

    if (isLoggedIn && !isInStaffPick && !isRecommended) {
      featuredTools.push(toolsForCategory[i]);
    }

    if (!isLoggedIn && !isInStaffPick && !isIntent) {
      featuredTools.push(toolsForCategory[i]);
    }
  }

  /*
    When `toolsForCategory` array has 3 apps and 2 are in the `recommendedApps` array, we
    will only have one app here in the `featuredTools` array. From the requirements, we need
    to have at least 2 featured apps per pillar / category. At this point, we know that:

      - The `recommendedApps` array will contain the first 2 from `toolsForCategory` array
      - The `featuredTools` array will never contain any of the `recommendedApps` array

    Therefore, we can just push the first app from `toolsForCategory` array to `featuredTools.
  */
  // the first app in toolsForCategory should not be pushed to featuredTools if featuredTool already had that app
  if (
    featuredTools.length < numRecommendations &&
    toolsForCategory[0] !== featuredTools[0]
  ) {
    featuredTools.push(toolsForCategory[0]);
  }

  return featuredTools;
};

const buildIntentEligibleLoggedOutMessages = (
  tools,
  { productIntentTools, isNewLayoutEligible }
) => {
  const intentHashMap = {};

  productIntentTools.forEach(({ name, totalCount90Day }) => {
    intentHashMap[name] = totalCount90Day;
  });

  return tools
    .filter(tool => intentHashMap[tool.appKey])
    .sort(
      (toolA, toolB) =>
        (intentHashMap[toolB.appKey] || 0) - (intentHashMap[toolA.appKey] || 0)
    )
    .slice(0, isNewLayoutEligible ? 3 : 4);
};

const buildGetStartedIntentEligibleMessages = (
  tools,
  { getStartedMsg, productIntentTools }
) => {
  const appRankObj = getStartedMsg.reduce(
    (obj, item) => Object.assign(obj, { [item.appKey]: item.rank }),
    {}
  );

  const appKeyArr = productIntentTools.map(({ name }) => name);

  return tools
    .filter(
      tool => appKeyArr.includes(tool.appKey) && tool.appKey in appRankObj
    )
    .sort(
      (toolA, toolB) =>
        (appRankObj[toolA.appKey] || 0) - (appRankObj[toolB.appKey] || 0)
    )
    .slice(0, 4);
};

const buildIntentMessages = (
  tools,
  productIntentTools,
  getStartedMsg,
  { isIntentEligible, isLoggedIn, isGetStartedEligible, isNewLayoutEligible }
) => {
  // Filter out the first 4 tools if present
  // Order tools by total view Count 90Day
  let intentMessages = [];

  if (isIntentEligible && !isLoggedIn) {
    intentMessages = buildIntentEligibleLoggedOutMessages(tools, {
      productIntentTools,
      isNewLayoutEligible
    });
  }

  if (isGetStartedEligible) {
    if (isIntentEligible) {
      intentMessages = buildGetStartedIntentEligibleMessages(tools, {
        getStartedMsg,
        productIntentTools
      });
    }

    const defaultGetStartedApps = getDefaultGetStartedApps(
      getStartedMsg,
      tools
    );

    defaultGetStartedApps.forEach(tool => {
      if (
        intentMessages.length < 4 &&
        !find(intentMessages, { appKey: tool.appKey })
      ) {
        intentMessages.push(tool);
      }
    });
  }

  return intentMessages;
};

const handleCategory = (
  category,
  sections,
  tools,
  recommendations,
  {
    isLoggedIn,
    staffPickCategory,
    staffPickAppKeys,
    intentMessages,
    isStaffPickEligible,
    isNewLayoutEligible
  }
) => {
  const newCategory = { content: category };
  const { categoryKey = '' } = category;
  const relevantSections = getSectionsForCategory(categoryKey, sections, tools);
  const toolsForCategory = getTools(categoryKey, tools);
  const intentRowKeys = intentMessages.map(item => item.appKey);
  const intentRowCategory = getIntentCategories(intentRowKeys, tools);
  const featuredTools = getFeaturedTools(
    categoryKey,
    toolsForCategory,
    recommendations,
    {
      staffPickCategory,
      staffPickAppKeys,
      isLoggedIn,
      intentRowKeys,
      intentRowCategory,
      isStaffPickEligible,
      isNewLayoutEligible
    }
  );

  newCategory.featured = featuredTools;
  if (relevantSections.length) {
    newCategory.sections = relevantSections;
  } else {
    newCategory.tools = toolsForCategory;
  }
  return newCategory;
};

export {
  getSectionsForCategory,
  getTools,
  isNotRecommendedStaffPicked,
  isNotRecommendedStaffPickedCategory,
  shouldReturnFirst2Apps,
  getFeaturedTools,
  buildIntentEligibleLoggedOutMessages,
  buildGetStartedIntentEligibleMessages,
  buildIntentMessages,
  handleCategory
};
